

















































































import Vue from 'vue'
import { mapGetters } from 'vuex'
import CloseButton from '@/components/CloseButton.vue'
import VueRecaptcha from 'vue-recaptcha'
import tenantNavMixin from '@/mixins/tenant-nav-mixin'
import Credentials from '@/types/Credentials'
export default Vue.extend({
    metaInfo: {
        title: "Log In to MessageDesk | Let's Start Some Conversations",
        meta: [
            {
                vmid: 'description',
                name: 'description',
                content:
                    'Log in to MessageDesk. Feel free to visit our learning center for tips, tricks, and how-to articles.'
            }
        ]
    },
    components: { CloseButton, VueRecaptcha },
    mixins: [tenantNavMixin],
    data: () => ({
        showPassword: false,
        loading: false,
        //  Styles for our elements at the foot of the page
        footerStyle: {
            position: 'absolute',
            left: 0,
            right: 0,
            margin: 'auto',
            display: 'block'
        },
        form: {
            email: '',
            password: '',
            created_at: '',
        },
        hk: process.env.VUE_APP_HASH_KEY,
        mobile: null,
        submitting: false,
        recaptchaAllowedAttempts: 3,
        recaptchaAttempts: 0,
        recaptchaKey: process.env.VUE_APP_RECAPTCHA_KEY, 
    }),
    mounted() {
        if (this.$store.getters.isMobile()) this.mobile = true
        else this.mobile = false
    },
    computed: {
        ...mapGetters([
            'isApp',
            'recaptchaVerified',
            'recaptchaVerifiedTime',
            'showRecaptcha'
        ]),
        linkTarget(): string {
            //  If we are on app, use system, else use blank
            return this.isApp ? '_system' : '_blank'
        },
        termsLink(): string {
            return `${process.env.VUE_APP_URL_SITE}/terms`
        },
        privacyLink(): string {
            return `${process.env.VUE_APP_URL_SITE}/privacy`
        },
        usePolicyLink(): string {
            return `${process.env.VUE_APP_URL_SITE}/use-agreement`
        },
        googlePrivacy(): string {
            return `https://policies.google.com/privacy`
        },
        googleTerms(): string {
            return `https://policies.google.com/terms`
        }
    },
    methods: {
        async encryptForm(): Promise<Credentials> {
            const encoder = new TextEncoder()
            const secretBuffer = encoder.encode(this.hk)

            const hmacKey = await window.crypto.subtle.importKey(
                'raw',
                secretBuffer,
                {name: 'HMAC', hash: 'SHA-256'},
                false,
                ['sign']
            )

            this.form.created_at = Date.now()

            const credentials = encoder.encode(JSON.stringify(this.form))

            const signatureBuffer = await window.crypto.subtle.sign(
                'HMAC',
                hmacKey,
                credentials
            )

            return {
                hash: btoa(String.fromCharCode(...new Uint8Array(signatureBuffer))),
                email: this.form.email,
                password: this.form.password,
                created_at: this.form.created_at
            }
        },
        async signin(): Promise<void> {
            //  Validate the form
            const valid = await this.$validator.validateAll()

            //  If the form is NOT valid, exit function
            if (!valid) return

            //  Start the loading spinner
            this.loading = true

            //  Check if recaptcha is already verified (ie verified OR last verification was under two minutes ago
            const isVerified =
                this.recaptchaVerified ||
                Number(new Date()) - this.recaptchaVerifiedTime < 120000

            //  Check if we should use Recaptcha
            const useRecaptcha = this.showRecaptcha && !isVerified

            //  If we need to use Recaptcha, do it! rather than signin
            if (useRecaptcha) return this.$refs.recaptcha.execute()

            const encryptedForm = await this.encryptForm()

            //  Send the login request
            const response = await this.$store.dispatch('login', encryptedForm)

            //  Stop the loading spinner
            this.loading = false

            //  If no errors were returned, routelogin
            if (response.user && response.twoFactorPassed) return this.routeLogin(response.user)
            //  In case a user logs out during registration we need to navigate them back to the registration workflow
            if (response.user && response.user.memberships.length == 0) return this.routeLogin(response.user)
            //  If password and username are correct but they haven't passed two factor auth route them to two factor
            //  auth workflow
            if (response.user && !response.twoFactorPassed) return this.routeTwoFactor()

            //  Else, if the error status is 401 pop error
            if (response.error.status === 401) {
                this.$store.commit('appNotificationBarSet', {
                    icon: 'mdi-alert',
                    message: 'Username or Password is not correct',
                    position: 'bottom',
                    type: 'error'
                })
            }

            //  Else, if the error status is 401 pop error
            if (response.error.status === 429) {
                this.$store.commit('appNotificationBarSet', {
                    icon: 'mdi-alert',
                    message: 'Too many login attempts, please try again later',
                    position: 'bottom',
                    type: 'error'
                })
            }
        },
        async onCaptchaVerified(token: number): Promise<void> {
            // Set the submission flag to true to disable the submit button
            this.submitting = true

            // Reset the reCAPTCHA widget
            this.$refs.recaptcha.reset()

            // Verify the reCAPTCHA token
            const success = await this.$store.dispatch('verifyRecaptcha', token)

            // If NOT successful (reCAPTCHA thinks user is a bot), pop the challenge (misnomer as we don't actually pop a typical recaptcha 'enter what you see above' screen)
            if (!success) {
                // We increment the total number of attempts so far, we need to do this as the previous code caused an infinite loop with unlimited retry attempts causing the server to crash when the api was down
                this.recaptchaAttempts++
                
                // If our total attempts is below the allowed amount we retry the recaptcha in case that the recapcha failed due to an accidental reason like user misinput
                if (this.recaptchaAttempts < this.recaptchaAllowedAttempts) this.$refs.recaptcha.execute()
                
                // Otherwise we return out of this function ending the endless loop and preventing server resources being 100% utilized and crashing the app. if they want to retry signing in they will then need to refresh the page
                if (this.recaptchaAttempts >= this.recaptchaAllowedAttempts) return
            }

            // Set the submission flag to true to enable the submit button
            this.submitting = false

            // If successful (reCAPTCHA thinks user is human), submit
            this.signin()
        },
        onCaptchaExpired(): void {
            // Reset the reCAPTCHA widget
            this.$refs.recaptcha.reset()
        },
        registrationRedirect(): void {
            //  Redirect user to tal to sales page, MCS will now give SLG leads the /register/user link to prevent accidental PLG sign ups
            window.open(
                'https://www.messagedesk.com/talk-to-sales',
                this.$store.getters.isApp ? '_system' : '_blank'
            )
        }
    }
})
